import { computed, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { init, track } from '@plausible-analytics/tracker';
import { Environment } from '../interfaces/environment.interface';
import { ENVIRONMENT } from '../tokens/environment.token';
import { PLAUSIBLE_DOMAIN } from '../tokens/plausible-domain.token';
import { USER_CONTEXT } from '../tokens/user-context.token';
import { activatedRoutePattern, normalizePlausibleUrl } from '../utils/plausible-url.util';

@Injectable()
export class PlausibleService {
	private router = inject(Router);
	private environment = inject<Environment>(ENVIRONMENT);
	private userContext = inject(USER_CONTEXT);
	private domain = inject<string>(PLAUSIBLE_DOMAIN);

	private role = computed(() => {
		return this.user() ? this.user()!.role : 'unknown';
	});

	private user = this.userContext.user;

	private initialized = false;

	constructor() {
		this.initialize();
		this.startRouterListener();
	}

	private shouldEnable() {
		return this.environment.production;
	}

	public initialize() {
		if (!this.shouldEnable() || this.initialized) {
			return;
		}
		init({
			domain: this.domain,
			autoCapturePageviews: false,
			transformRequest: (payload) => ({ ...payload, u: this.normalizeUrl(payload.u) }),
		});
		this.initialized = true;
	}

	private normalizeUrl(url: string) {
		return normalizePlausibleUrl(url, activatedRoutePattern(this.router.routerState.snapshot.root));
	}

	private startRouterListener() {
		this.router.events
			.pipe(
				takeUntilDestroyed(),
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe((event) => {
				this.trackPageView((event as NavigationEnd).urlAfterRedirects);
			});
	}

	private trackPageView(url: string) {
		if (!this.shouldEnable()) return;

		track('pageview', {
			url: window.location.origin + url,
			props: { role: this.role() },
		});
	}

	public eventEmitter(eventName: string, label: any = null) {
		if (!this.shouldEnable()) {
			return;
		}

		track(eventName, {
			props: {
				label: label || '',
				page: this.router.url,
				role: this.role(),
			},
		});
	}
}
